import { ConvertDates, FirestoreTimestamp } from '@griply/types';

const isRecord = (val: unknown): val is Record<string, unknown> =>
  val !== null &&
  typeof val === 'object' &&
  !Array.isArray(val) &&
  !(val instanceof Date);

/**
 * Recursively converts JavaScript Date objects to Firestore Timestamps.
 * Preserves all other value types. Skips undefined values.
 */
export const dateToFirestoreTimestamp = <T, TTimestamp = FirestoreTimestamp>(
  obj: T,
  timestampFromDate: (date: Date) => TTimestamp,
): ConvertDates<T, TTimestamp> => {
  // Cast: TS can't track runtime narrowing (instanceof/Array.isArray/isRecord) through ConvertDates<T, TTimestamp>.
  return convert(obj, timestampFromDate) as ConvertDates<T, TTimestamp>;
};

const convert = <TTimestamp>(
  obj: unknown,
  timestampFromDate: (date: Date) => TTimestamp,
): unknown => {
  if (obj instanceof Date) {
    return timestampFromDate(obj);
  }

  if (Array.isArray(obj)) {
    return obj
      .map((item) => convert(item, timestampFromDate))
      .filter((item) => item !== undefined);
  }

  if (isRecord(obj)) {
    const transformed: Record<string, unknown> = {};

    for (const key of Object.keys(obj)) {
      const val = convert(obj[key], timestampFromDate);

      if (val === undefined) {
        continue;
      }

      transformed[key] = val;
    }

    return transformed;
  }

  return obj;
};
